import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import { CreateRapForecast, UpdateRapForecast } from '@repo/backend/crt'
import {
  PRODUCT_CATEGORIES,
  RAP_FORECASTS_CREATE_ERROR_TYPES,
  YEAR_FLAG_VALUES,
} from '@repo/constants'
import { toastClassName } from '@repo/ui/lib/constants'
import { monthIndex } from '@repo/zod-schemas'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

export const rapForecastsSearchSchema = z.object({
  keyword: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export const rapForecastDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  year: z.enum(YEAR_FLAG_VALUES).optional(),
  month: monthIndex.optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  budgetAllocation: z.array(z.string()).optional(),
  conditionType: z.array(z.string()).optional(),
  masterAgreement: z.array(z.string()).optional(),
  agreementNumber: z.array(z.string()).optional(),
  investmentType: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})

export type RapForecastsSearch = z.infer<typeof rapForecastsSearchSchema>
export type RapForecastDetailsSearch = z.infer<typeof rapForecastDetailsSearchSchema>

export type RapForecastsList = Awaited<ReturnType<typeof getRapForecastsList>>
export type RapForecastsItem = RapForecastsList['results'][number]
export type RapForecastsWithDetails = Awaited<ReturnType<typeof getRapForecastById>>

type UseCreateRapForecastsArgs = {
  queries: RapForecastsSearch
  handleOnSuccess: (rapForecastId: number) => void
}

type UseUpdateRapForecastsArgs = QueryHandlers & {
  rapForecastId: number
}

type UseDeleteRapForecastsArgs = QueryHandlers & {
  rapForecastIds: number[]
  queries: RapForecastsSearch
}

async function getCurrentRapForecast(crtPeriod?: string, signal?: AbortSignal) {
  const api = getApi()
  const query = crtPeriod ? { crtPeriod } : {}
  const res = await api.crt['rap-forecasts'].current.$get({ query }, { init: { signal } })
  return await res.json()
}

export const currentRapForecastQueryOptions = (crtPeriod?: string) => ({
  queryKey: ['currentRapForecast', crtPeriod ?? null] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getCurrentRapForecast(crtPeriod, signal),
})

export function useCurrentRapForecast(crtPeriod?: string) {
  return useQuery(currentRapForecastQueryOptions(crtPeriod))
}

async function getLatestRapForecast(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['rap-forecasts'].latest.$get({}, { init: { signal } })
  return await res.json()
}

export function useLatestRapForecast() {
  return useQuery({
    queryKey: ['latestRapForecast'] as const,
    queryFn: ({ signal }) => getLatestRapForecast(signal),
  })
}

async function getRapForecastsList(search: RapForecastsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['rap-forecasts'].$get(
    {
      query: stringifyValues(search),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function getRapForecastById(
  id: string,
  queries: RapForecastDetailsSearch = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['rap-forecasts'][':id'].$get(
    {
      param: { id },
      query: stringifyValues(queries),
    },
    { init: { signal } },
  )

  return await res.json()
}

async function createRapForecast(rapForecastDetails: CreateRapForecast) {
  const api = getApi()

  const res = await api.crt['rap-forecasts'].$post({
    json: rapForecastDetails,
  })

  return await res.json()
}

async function updateRapForecast(rapForecastId: number, details: UpdateRapForecast) {
  const api = getApi()

  await api.crt['rap-forecasts'][':id'].$patch({
    param: { id: String(rapForecastId) },
    json: details,
  })
}

async function deleteRapForecasts(rapForecastIds: number[]) {
  const api = getApi()

  await api.crt['rap-forecasts'].bulk.$delete({
    json: { ids: rapForecastIds },
  })
}

async function getRapForecastDetailsRawCsv(id: string, signal?: AbortSignal) {
  return fetchCsvExport(`/crt/rap-forecasts/${id}/raw-export`, signal)
}

async function getRapForecastDetailsCsv(
  id: string,
  queries: RapForecastDetailsSearch = {},
  signal?: AbortSignal,
) {
  const stringifiedQueries = stringifyValues(queries)

  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()

  return fetchCsvExport(`/crt/rap-forecasts/${id}/export?${queryParams}`, signal)
}

export const rapForecastDetailsRawExportQueryOptions = (id: string) => ({
  queryKey: ['rapForecastDetailsRawExport', id],
  queryFn: ({ signal }: { signal: AbortSignal }) => getRapForecastDetailsRawCsv(id, signal),
})

export const rapForecastDetailsExportQueryOptions = (
  id: string,
  queries: RapForecastDetailsSearch,
) => ({
  queryKey: ['rapForecastDetailsExport', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getRapForecastDetailsCsv(id, queries, signal),
})

export function useRapForecasts(queries: RapForecastsSearch) {
  return useQuery({
    queryKey: ['rapForecasts', queries],
    queryFn: ({ signal }) => getRapForecastsList(queries, signal),
  })
}

export const rapForecastByIdQueryOptions = (
  id: string,
  queries: RapForecastDetailsSearch = {},
) => ({
  queryKey: ['rapForecastById', id, queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getRapForecastById(id, queries, signal),
})

export function useRapForecastById(id: string, queries: RapForecastDetailsSearch) {
  return useQuery(rapForecastByIdQueryOptions(id, queries))
}

export function useCreateRapForecast({ queries, handleOnSuccess }: UseCreateRapForecastsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['rapForecasts', queries],
    mutationFn: createRapForecast,
    onSuccess: (rapForecast) => {
      handleOnSuccess(rapForecast.id)

      queryClient.invalidateQueries({
        queryKey: ['rapForecasts', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('rapForecasts.createSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const customParams = issues?.find((i) => i.code === 'custom')?.params as
        | { type?: string; crtPeriod?: string }
        | undefined

      let message: string
      switch (customParams?.type) {
        case RAP_FORECASTS_CREATE_ERROR_TYPES.crtPeriodConflict:
          message = `${t('errors.rapForecasts.createFailed', { ns: 'crt' })}: ${t(
            'errors.rapForecasts.crtPeriodConflict',
            { ns: 'crt', crtPeriod: customParams.crtPeriod },
          )}`
          break
        default:
          message = getFirstZodErrorMessage(issues, t, 'errors.rapForecasts.createFailed', 'crt')
      }

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useUpdateRapForecast({
  rapForecastId,
  handleOnSuccess,
}: UseUpdateRapForecastsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['updateRapForecast', rapForecastId],
    mutationFn: (details: UpdateRapForecast) => updateRapForecast(rapForecastId, details),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['rapForecastById', String(rapForecastId)],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('rapForecasts.updateSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.rapForecasts.updateFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export function useDeleteRapForecasts({
  rapForecastIds,
  queries,
  handleOnSuccess,
}: UseDeleteRapForecastsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteRapForecasts'],
    mutationFn: () => deleteRapForecasts(rapForecastIds),
    onSuccess: () => {
      if (handleOnSuccess) {
        handleOnSuccess()
      }

      queryClient.invalidateQueries({
        queryKey: ['rapForecasts', queries],
      })

      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('rapForecasts.deleteSuccess', { ns: 'crt' }),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const issues = extractZodIssues(error)
      const message = getFirstZodErrorMessage(issues, t, 'errors.rapForecasts.deleteFailed', 'crt')

      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}
